import { Component, HostListener, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, from, of } from 'rxjs';
import { catchError, concatMap, filter, finalize, take } from 'rxjs/operators';
import { MagazineService } from '../../core/services/magazine.service';
import { Magazine } from '../../core/models/magazine.model';
import { resolveMagazinePdfCandidates, magazineCoverSrc } from '../../shared/utils/magazine.util';
import { ReadingActivityService } from '../../core/services/reading-activity.service';
import { BookLoaderComponent } from '../../shared/components/book-loader/book-loader.component';
import { fadeInUp } from '../../shared/animations';

@Component({
  standalone: true,
  imports: [RouterLink, BookLoaderComponent],
  templateUrl: './magazine-viewer-page.component.html',
  animations: [fadeInUp]
})
export class MagazineViewerPageComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly magazineService = inject(MagazineService);
  private readonly readingActivity = inject(ReadingActivityService);

  readonly loading = signal(true);
  readonly pdfLoading = signal(false);
  readonly magazine = signal<Magazine | null>(null);
  readonly pdfSrc = signal<SafeResourceUrl | null>(null);
  readonly pdfError = signal('');
  readonly toolbarVisible = signal(true);

  private objectUrl: string | null = null;
  private navSub?: Subscription;
  private pdfSub?: Subscription;
  private hideTimer = 0;
  private lastTrackedId = 0;

  ngOnInit(): void {
    document.documentElement.classList.add('magazine-viewer-open');
    this.loadMagazineFromRoute();
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.loadMagazineFromRoute());
    this.scheduleToolbarHide();
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('magazine-viewer-open');
    this.navSub?.unsubscribe();
    this.pdfSub?.unsubscribe();
    this.revokeObjectUrl();
    if (this.hideTimer) window.clearTimeout(this.hideTimer);
  }

  @HostListener('document:pointerdown')
  onPointer(): void {
    this.toolbarVisible.set(true);
    this.scheduleToolbarHide();
  }

  private scheduleToolbarHide(): void {
    if (this.hideTimer) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.toolbarVisible.set(false), 2200);
  }

  private loadMagazineFromRoute(): void {
    const id = Number(this.route.snapshot.paramMap.get('id') || 0);

    if (!id) {
      this.magazine.set(null);
      this.clearPdf();
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.pdfError.set('');
    this.lastTrackedId = 0;
    const cached = this.magazineService.getById(id);
    if (cached) {
      this.magazine.set(cached);
      this.trackMagazine(cached);
      this.loadPdf(cached);
    }

    this.magazineService
      .loadMagazineById(id)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (mag) => {
          if (mag) {
            this.magazine.set(mag);
            this.trackMagazine(mag);
            this.loadPdf(mag);
          } else if (!cached) {
            this.magazine.set(null);
            this.clearPdf();
          }
        },
        error: () => {
          if (!cached) {
            this.magazine.set(null);
            this.clearPdf();
          } else {
            this.loadPdf(cached);
          }
        }
      });
  }

  private trackMagazine(mag: Magazine): void {
    if (!mag?.MagId || this.lastTrackedId === mag.MagId) return;
    this.lastTrackedId = mag.MagId;
    this.readingActivity.trackMagazine({
      id: mag.MagId,
      title: mag.MagTitle,
      issue: mag.MagIssueNumber || mag.MagGenre || 'Magazine',
      imageUrl: magazineCoverSrc(mag) || ''
    });
  }

  /** Try API stream then MagUrl static paths; only embed real PDF bytes. */
  private loadPdf(mag: Magazine): void {
    this.pdfSub?.unsubscribe();
    this.pdfLoading.set(true);
    this.pdfError.set('');
    this.clearPdf();

    const candidates = resolveMagazinePdfCandidates(mag);
    if (!candidates.length) {
      this.pdfLoading.set(false);
      this.pdfError.set('This issue does not have a readable PDF yet.');
      return;
    }

    this.pdfSub = from(candidates)
      .pipe(
        concatMap((url) => this.tryFetchPdf(url)),
        filter((blob): blob is Blob => !!blob),
        take(1),
        finalize(() => this.pdfLoading.set(false))
      )
      .subscribe({
        next: (blob) => {
          this.revokeObjectUrl();
          this.objectUrl = URL.createObjectURL(blob);
          this.pdfSrc.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));
        },
        complete: () => {
          if (!this.pdfSrc()) {
            this.pdfError.set('Magazine PDF could not be loaded. Please try again later.');
          }
        }
      });
  }

  private tryFetchPdf(url: string): Observable<Blob | null> {
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      concatMap(async (res) => {
        const blob = res.body;
        if (!blob || blob.size < 5) return null;
        const type = (blob.type || res.headers.get('content-type') || '').toLowerCase();
        if (type.includes('html') || type.includes('json') || type.includes('text/')) return null;
        const header = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
        const magic = String.fromCharCode(...Array.from(header));
        if (!magic.startsWith('%PDF')) return null;
        return new Blob([blob], { type: 'application/pdf' });
      }),
      catchError(() => of(null))
    );
  }

  private clearPdf(): void {
    this.revokeObjectUrl();
    this.pdfSrc.set(null);
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
